import type { UseFormReturn} from '@ur-solutions/react-hooks';
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  TextArea,
  TimePicker,
  UserSelect,
} from '@/components_deprecated'
import { Form, FormField } from '@/components_deprecated/Form'
import { DepartmentSelect } from '@/components_deprecated/Select/DepartmentSelect'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import { TimeEntryProjectSelect } from '@/components_deprecated/Select/TimeEntryProjectSelect'
import { parseISO, set } from 'date-fns'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import styled from 'styled-components'
import { useModuleConfig, usePermissions } from '@/utils/hooks'
import type { TimeEntryFormDates, TimeEntryFormValues } from '../types'
import type {
  TimesheetsDayReportTimeEntry,
  TimesheetsSalaryWorkTime,
} from '../types.graphql'
import { PERMISSIONS } from '@/utils/permissions'

const StyledForm = styled(Form)`
  width: 100%;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StyledFormField = styled(FormField)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  label {
    color: ${props => props.theme.colors.gray3};
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }
`

const HorizontalFields = styled.div`
  display: flex;
  flex-direction: row;

  & > div:first-child {
    margin-right: 1rem;
    flex-grow: 1;
  }
`
/*
const ExtraCheckboxFormField = styled(StyledFormField)`
  width: auto;
  gap: 6px;

`
*/

const DateTimeField = styled(StyledFormField)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
  ${props => props.theme.media.mobile} {
    .--time-picker-inner-wrapper {
      width: 80px;
      padding: 0;
    }
  }
`
const ErrorDisplay = styled(FormField).attrs({
  name: 'error',
})`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.75rem;
  display: flex;
  justify-content: flex-end;

  div.error {
    display: flex;
    align-items: center;

    margin-right: 1rem;
    color: ${props => props.theme.colors.red};
  }

  ${props => props.theme.media.mobile} {
    flex-direction: column;

    div.error {
      justify-content: center;
      margin-right: 0;
      margin-bottom: 1rem;
    }
  }
`

interface TimeEntryFormProps {
  form: UseFormReturn<TimeEntryFormValues>
  overtimeCustomChoice?: boolean
  salaryWorkTime: TimesheetsSalaryWorkTime
  existingTimeEntries: Pick<
    TimesheetsDayReportTimeEntry,
    'datetimeStart' | 'datetimeEnd'
  >[]
  canSetSalaryCodeManually: boolean
  onSubmit: (values: TimeEntryFormValues) => void
}

export const TimeEntryForm: React.FC<TimeEntryFormProps> = ({
  form,
  overtimeCustomChoice,
  salaryWorkTime,
  existingTimeEntries,
  canSetSalaryCodeManually,
  onSubmit,
}) => {
  const translations = useTranslate({
    user: 'common.user',
    project: 'common.project',
    department: 'common.department',
    newTimeEntry: 'timesheets.new-time-entry',
    createTimeEntry: 'timesheets.register-timeentry',
    from: 'common.from',
    until: 'common.until',
    comment: 'common.comment',
    needsComment: 'common.needs-comment',
    overtimeMode: 'common.overtime-mode',
    diet: 'common.diet',
    additionalPay: 'common.additional-pay',
    pause: 'common.pause',
    salaryCode: 'common.salary-code',

    cancel: 'common.cancel',
    submit: 'common.submit',
    success: 'timesheets.success-registration-timeentry',

    error: {
      create: 'timesheets.error-create-timeentry',
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
      overtimeMode: 'timesheets.error.overtime-mode-required',
    },
    warning: {
      create: 'timesheets.warning.time-entry-exist-in-daterange',
    },
  })

  const { hasPermissions } = usePermissions()

  const canSeeOthers = hasPermissions(
    PERMISSIONS.timesheets.view.otherstimeentry,
    true
  )

  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)

  const {
    formValues: values,
    formErrors: errors,
    formChangeHandler: handler,
    updateForm,
    submitHandler,
  } = form

  function updateTime(key: keyof TimeEntryFormDates) {
    return (value: Date) => {
      const existingValue = values[key]
      const newValue = set(existingValue, {
        hours: value.getHours(),
        minutes: value.getMinutes(),
      })

      updateForm({
        [key]: newValue,
        overtimeSalaryCodeId: null,
      })
    }
  }

  function updateDate(key: keyof TimeEntryFormDates) {
    return (value: Date) => {
      const existingValue = values[key]
      const newValue = set(existingValue, {
        year: value.getFullYear(),
        month: value.getMonth(),
        date: value.getDate(),
      })

      let newEnd: { end?: Date } = {}
      if (key === 'start')
        newEnd = {
          end: set(values.end, {
            year: value.getFullYear(),
            month: value.getMonth(),
            date: value.getDate(),
          }),
        }

      updateForm({
        ...newEnd,
        [key]: newValue,
        overtimeSalaryCodeId: null,
      })
    }
  }

  const projectActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_PROJECTS
  )

  const salaryCodeActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_SALARY_CODE_IN_REGISTRATION
  )

  const existingWorkingMs = existingTimeEntries.reduce(
    (acc, entry) =>
      acc +
      ((!entry.datetimeEnd
        ? new Date()
        : parseISO(entry.datetimeEnd)
      ).getTime() -
        parseISO(entry.datetimeStart).getTime()),
    0
  )
  const thisWorkingMs = values.end.getTime() - values.start.getTime()
  const totalWorkingSeconds = (existingWorkingMs + thisWorkingMs) / 1000

  const overtimeCustomChoiceActive =
    overtimeCustomChoice && totalWorkingSeconds > salaryWorkTime.dailyWorkLimit

  const errorActive =
    !!errors.start ||
    !!errors.end ||
    (overtimeCustomChoiceActive &&
      form.formValues.overtimeSalaryCodeId === null)

  return (
    <StyledForm preventDefault onSubmit={submitHandler(onSubmit)}>
      {canSeeOthers && (
        <StyledFormField>
          <label>{translations.user}</label>
          <UserSelect
            value={form.formValues.userId}
            grayed
            onChange={userId =>
              !userId ? void 0 : form.updateForm({ userId })
            }
          />
        </StyledFormField>
      )}
      <DateTimeField>
        <label>{translations.from}</label>
        <TimePicker
          value={values.start}
          width="100px"
          onChange={updateTime('start')}
          grayed
        />

        <DatePicker
          value={values.start}
          fullWidth
          onChange={updateDate('start')}
          grayed
        />
      </DateTimeField>

      <DateTimeField>
        <label>{translations.until}</label>
        <TimePicker
          value={values.end}
          width="100px"
          onChange={updateTime('end')}
          grayed
        />
        <DatePicker
          value={values.end}
          fullWidth
          onChange={updateDate('end')}
          grayed
        />
      </DateTimeField>
      {totalWorkingSeconds >= salaryWorkTime.pauseThreshold && (
        <StyledFormField>
          <label>{translations.pause}</label>
          <TimePicker
            value={values.pause}
            width="100%"
            onChange={handler('pause')}
            grayed
          />

          {!!errors.pause && (
            <ErrorDisplay>
              <div className="error">{errors.pause}</div>
            </ErrorDisplay>
          )}
        </StyledFormField>
      )}
      {salaryCodeActive && canSetSalaryCodeManually && (
        <HorizontalFields>
          <StyledFormField>
            <label>{translations.salaryCode}</label>
            <SalaryCodeSelect
              fullWidth
              nullable
              placeholder={translations.salaryCode}
              value={form.formValues.salaryCodeId}
              onChange={handler('salaryCodeId')}
              searchable={true}
              grayed
            />
          </StyledFormField>
        </HorizontalFields>
      )}

      {overtimeCustomChoiceActive && (
        <StyledFormField>
          <label>{translations.additionalPay}</label>
          <SalaryCodeSelect
            fullWidth
            nullable
            placeholder={translations.additionalPay}
            value={form.formValues.overtimeSalaryCodeId}
            showInManualTimeEntryOvertime={true}
            onChange={handler('overtimeSalaryCodeId')}
            grayed
          />
          {errorActive && (
            <ErrorDisplay>
              {(!!errors.start || !!errors.end) && (
                <div className="error">{errors.start ?? errors.end}</div>
              )}
              {overtimeCustomChoiceActive &&
                form.formValues.overtimeSalaryCodeId === null && (
                  <div className="error">{translations.error.overtimeMode}</div>
                )}
            </ErrorDisplay>
          )}
        </StyledFormField>
      )}
      <StyledFormField>
        <label>{translations.comment}</label>
        <TextArea
          value={values.comment}
          placeholder={translations.needsComment}
          onChange={handler('comment')}
          fullWidth
        />
      </StyledFormField>
      <StyledFormField>
        <label>{translations.diet}</label>
        <Checkbox
          boxSize="44px"
          checked={values.diet}
          onChange={handler('diet')}
          grayed
        />
      </StyledFormField>
      <StyledFormField>
        <label>{translations.department}</label>
        <DepartmentSelect
          onChange={handler('department')}
          value={values.department}
          error={form.formErrors.department}
          grayed
          nullable
          searchable
          dataset={salaryWorkTime.availableDepartments}
          alwaysInclude={salaryWorkTime.defaultDepartment ?? undefined}
        />
      </StyledFormField>
      {projectActive && (
        <StyledFormField>
          <label>{translations.project}</label>
          <TimeEntryProjectSelect
            value={values.project}
            onChange={handler('project')}
            background="quaternary"
            optionHoverBackground="white"
            grayed
            nullable
          />
        </StyledFormField>
      )}
    </StyledForm>
  )
}
